/* global __DEV__ */
import {
  initialize,
  LDClient,
  LDFlagSet,
  LDFlagValue,
  LDUser,
} from 'ldclient-js';

import { Storage } from '@trello/storage';
import {
  launchdarklyKey,
  atlassianFeatureFlagClientKey,
  environment,
} from '@trello/config';
import { memberId } from '@trello/session-cookie';
import { Analytics } from '@trello/atlassian-analytics';
import { isDesktop, isTouch } from '@trello/browser';
import AtlassianFeatureFlagClient, {
  Identifiers,
  EnvironmentType,
  FeatureFlagUser,
  SupportedFlagTypes,
} from '@atlassiansox/feature-flag-web-client';

const USER_DATA_CACHE_TIMEOUT = 24 * 60 * 60 * 1000;
const USER_DATA_CACHE_STORAGE_KEY = 'featureFlagUserData';
const OVERRIDES_STORAGE_KEY = 'featureFlagOverrides';

interface UserData {
  channel: string;
  idEnterprises: string[];
  idOrgs: string[];
  orgs: string[];
  emailDomain: string;
  clientVersion: string;
  head: string;
  version: number;
}

interface UserDataCache {
  lastUpdated: number;
  key: string;
  data: UserData;
}

interface ChangeListener<T> {
  (current?: T, previous?: T): void;
}
interface ClientListenerMap {
  [key: string]: ChangeListener<SupportedFlagTypes>;
}

interface ChangeListenerMap {
  [key: string]: ChangeListener<SupportedFlagTypes>[];
}

interface UnsubscriberMap {
  [key: string]: () => void;
}

export interface FlagSet {
  [key: string]: SupportedFlagTypes;
}

interface FeatureFlagGroups {
  remote: FlagSet;
  overrides: FlagSet;
}

export const FX3_EVALUATIONS_FLAG = 'fep.fx3-evaluations';

export class FeatureFlagClient {
  private user: LDUser;
  private atlassianClientUser: FeatureFlagUser;

  private clientListeners: ClientListenerMap = {};
  private atlassianClientUnsubscribers: UnsubscriberMap = {};
  private changeListeners: ChangeListenerMap = {};

  client: LDClient;
  atlassianClient: AtlassianFeatureFlagClient;
  pollingInterval: number = 5000;

  initializeLdClient(user: LDUser) {
    this.user = user;

    const userData: UserDataCache = Storage.get(USER_DATA_CACHE_STORAGE_KEY);
    const isUserDataExpired =
      userData && Date.now() - userData.lastUpdated > USER_DATA_CACHE_TIMEOUT;
    const sameUser = userData && userData.key === this.user.key;

    if (userData) {
      if (!isUserDataExpired && sameUser) {
        this.user = {
          ...this.user,
          custom: {
            ...this.user.custom,
            ...userData.data,
          },
        };
      } else {
        Storage.unset(USER_DATA_CACHE_STORAGE_KEY);
      }
    }

    this.client = initialize(launchdarklyKey, this.user, {
      bootstrap: 'localStorage',
      evaluationReasons: true,
      sendEventsOnlyForVariation: true,
      flushInterval: 60000,
    });
  }

  initializeAtlassianClient() {
    const identifierValue = memberId;

    let clientEnv = EnvironmentType.DEV;
    if (environment === 'staging') {
      clientEnv = EnvironmentType.STAGING;
    } else if (environment === 'prod') {
      clientEnv = EnvironmentType.PROD;
    }

    // Poll at 5 minutes in prod, 5 seconds otherwise
    this.pollingInterval =
      clientEnv === EnvironmentType.PROD ? 1000 * 60 * 5 : 5000;

    // Build up our flag user: we want to omit the `identifier` if the memberId
    // is not set, so that the client will generate a UUID
    if (identifierValue !== null) {
      this.atlassianClientUser = {
        identifier: {
          type: Identifiers.TRELLO_USER_ID,
          value: identifierValue,
        },
        isAnonymous: false,
      };
    } else {
      this.atlassianClientUser = {
        isAnonymous: true,
      };
    }

    this.atlassianClientUser.custom = {
      isDesktop: isDesktop(),
      isTouch: isTouch(),
    };

    try {
      this.atlassianClient = new AtlassianFeatureFlagClient(
        atlassianFeatureFlagClientKey,
        { sendOperationalEvent: () => {} }, // Isnt used for this release
        this.atlassianClientUser,
        {
          productKey: 'trello',
          environment: clientEnv, // Would be nice if we didn't need to provide this
          pollingInterval: this.pollingInterval,
        },
      );
    } catch (err) {
      console.error(
        'AtlassianFeatureFlagClient threw an error on initialisation.',
        err,
      );
    }
  }

  constructor(user: LDUser) {
    // Initialize the ldclient
    this.initializeLdClient(user);

    // Initialize the fx3 client
    this.initializeAtlassianClient();
  }

  refineUserData(data: UserData) {
    const cacheData: UserDataCache = {
      lastUpdated: Date.now(),
      key: this.user.key || 'notLoggedIn',
      data,
    };

    Storage.set(USER_DATA_CACHE_STORAGE_KEY, cacheData);

    this.atlassianClient.updateFeatureFlagUser({
      ...this.atlassianClientUser,
      custom: {
        ...this.atlassianClientUser.custom,
        ...data,
      },
    });

    this.client.identify({
      ...this.user,
      custom: {
        ...this.user.custom,
        ...data,
      },
    });
  }

  get = <T extends SupportedFlagTypes>(key: string, defaultValue: T) => {
    if (defaultValue === undefined && __DEV__) {
      console.warn(
        `Flag evaluation now requires a default value as the second argument to .get(). Please ensure a default value as the second argument when evaluating ${key}`,
      );
    }
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      return override;
    }

    if (this.client.variation(FX3_EVALUATIONS_FLAG, false)) {
      return this.atlassianClient.getFlagValue(key, defaultValue);
    } else {
      return this.client.variation(key, defaultValue);
    }
  };

  getTrackedVariation = <T extends SupportedFlagTypes>(
    key: string,
    defaultValue: T,
    attributes?: object,
  ) => {
    const override = this.getOverride(key);

    if (typeof override !== 'undefined') {
      FeatureFlagClient.triggerExposureEvent(key, override, 'OVERRIDE');
      return override;
    }

    if (this.client.variation(FX3_EVALUATIONS_FLAG, false)) {
      const evaluatedFlag = this.atlassianClient.getFlagDetails(
        key,
        defaultValue,
      );
      const value = evaluatedFlag.value;
      const evaluationDetail = evaluatedFlag.evaluationDetail;
      if (evaluationDetail) {
        const kind = evaluationDetail.reason;
        const ruleId = evaluationDetail.ruleId;

        FeatureFlagClient.triggerExposureEvent(
          key,
          value,
          kind,
          ruleId,
          attributes,
        );
      }
      return value;
    } else {
      const variationDetail = this.client.variationDetail(key, defaultValue);
      const { value, reason } = variationDetail;
      const { kind, ruleId } = reason;
      FeatureFlagClient.triggerExposureEvent(
        key,
        value,
        kind,
        ruleId,
        attributes,
      );
      return value;
    }
  };

  all(): FeatureFlagGroups {
    const overrides: FlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};
    if (this.client.variation(FX3_EVALUATIONS_FLAG, false)) {
      // Get the raw flags from atlassianClient
      const atlassianClientFlags = this.atlassianClient.getFlags();

      // Pluck the values off the flags (they come with some additional stuff we don't
      // care about here)
      const remote: FlagSet = {};
      for (const [key, { value }] of Object.entries(atlassianClientFlags)) {
        remote[key] = value;
      }

      return { remote, overrides };
    } else {
      const remote = this.client.allFlags();

      return {
        remote,
        overrides,
      };
    }
  }

  public static triggerExposureEvent(
    flagKey: string,
    value: SupportedFlagTypes,
    reason: string,
    ruleId?: string,
    attributes?: object,
  ) {
    Analytics.sendTrackEvent({
      source: '@trello/feature-flag-client',
      actionSubject: 'feature',
      action: 'exposed',
      attributes: {
        flagKey,
        reason,
        ruleId,
        value,
        ...attributes,
      },
    });
  }

  private createClientListener(
    key: string,
  ): ChangeListener<SupportedFlagTypes> {
    const clientListener = (
      current: SupportedFlagTypes,
      previous: SupportedFlagTypes,
    ) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        if (typeof this.getOverride(key) === 'undefined') {
          for (const listener of this.changeListeners[key]) {
            listener(current, previous);
          }
        } else {
          console.warn(
            `Ignoring feature flag change event for "${key}" from server due to local override`,
          );
        }
      }
    };

    return clientListener;
  }

  private getLdClientListener(key: string): ChangeListener<SupportedFlagTypes> {
    if (Object.prototype.hasOwnProperty.call(this.clientListeners, key)) {
      return this.clientListeners[key];
    }

    this.clientListeners[key] = this.createClientListener(key);
    return this.clientListeners[key];
  }

  on = <T extends SupportedFlagTypes>(
    key: string,
    defaultValue: T,
    callback: ChangeListener<T>,
  ) => {
    if (typeof defaultValue === 'function' && __DEV__) {
      console.warn(
        `Flag subscription now requires a default value as the second argument to .on(). Please ensure a default value as the second argument when subscribing to ${key}`,
      );
    }
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      this.changeListeners[key].push(callback);
    } else {
      // Create the first change listener
      this.changeListeners[key] = [callback];

      if (this.client.variation(FX3_EVALUATIONS_FLAG, false)) {
        const unsubscriber = this.atlassianClient.on(
          key,
          defaultValue,
          this.createClientListener(key),
        );
        this.atlassianClientUnsubscribers[key] = unsubscriber;
      } else {
        this.client.on(`change:${key}`, this.getLdClientListener(key));
      }
    }
  };

  off = (key: string, callback: ChangeListener<SupportedFlagTypes>) => {
    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      const idx = this.changeListeners[key].indexOf(callback);

      if (idx > -1) {
        this.changeListeners[key].splice(idx, 1);
      }

      if (this.changeListeners[key].length === 0) {
        const clientListener = this.getLdClientListener(key);

        // Unsubscribe from ldclient
        this.client.off(`change:${key}`, clientListener);
        delete this.clientListeners[key];

        // Unsubscribe from atlassianClient
        this.atlassianClientUnsubscribers[key]?.();
        delete this.atlassianClientUnsubscribers[key];
      }
    }
  };

  ready = () => {
    if (this.client.variation(FX3_EVALUATIONS_FLAG, false)) {
      return this.atlassianClient.ready();
    } else {
      return this.client.waitUntilReady();
    }
  };

  private getOverride(key: string): LDFlagValue {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY);

    if (overrides && Object.prototype.hasOwnProperty.call(overrides, key)) {
      return overrides[key];
    }
  }

  setOverride<T extends SupportedFlagTypes>(key: string, value: T): void {
    // We assume false as the default value here, as our overriding doesn't currently
    // support anything other than booleans
    const originalValue = this.get(key, false);
    const existingOverrides: LDFlagSet =
      Storage.get(OVERRIDES_STORAGE_KEY) || {};

    Storage.set(OVERRIDES_STORAGE_KEY, {
      ...existingOverrides,
      [key]: value,
    });

    if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
      // Call all change listeners for this feature flag
      for (const listener of this.changeListeners[key]) {
        listener(value, originalValue);
      }
    }
  }

  resetOverrides(): void {
    const overrides: LDFlagSet = Storage.get(OVERRIDES_STORAGE_KEY) || {};
    Storage.set(OVERRIDES_STORAGE_KEY, {});

    // Call change listeners for all overrides that were just reset
    Object.entries(overrides).forEach(([key, originalValue]) => {
      if (Object.prototype.hasOwnProperty.call(this.changeListeners, key)) {
        for (const listener of this.changeListeners[key]) {
          listener(this.get(key, false), originalValue);
        }
      }
    });
  }
}

export const featureFlagClient = new FeatureFlagClient({
  key: memberId || 'notLoggedIn',
  anonymous: !memberId,
  custom: {
    isDesktop: isDesktop(),
    isTouch: isTouch(),
  },
});
